import i18n from 'sveltekit-i18n'
import { LANGS, LANGS_SHORT } from '$lib/constants'
import type { LangKeys } from '$lib/types'
import type { Parser } from '@sveltekit-i18n/parser-default'

type Loader = {
  locale: string
  key: string
  routes?: (string | RegExp)[]
  loader: () => Promise<unknown>
}

export type Payload = Parser.PayloadDefault & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

const loaders = LANGS_SHORT.reduce((acc: Loader[], language: string) => {
  const loaders: Loader[] = []

  loaders.push({
    locale: language,
    key: 'common',
    loader: async () => (await import(`./langs/${language}/common.json`)).default
  })

  loaders.push({
    locale: language,
    key: 'errors',
    loader: async () => (await import(`./langs/${language}/errors.json`)).default
  })

  loaders.push({
    locale: language,
    key: 'home',
    loader: async () => (await import(`./langs/${language}/home.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'mybooking',
    loader: async () => (await import(`./langs/${language}/mybooking.json`)).default
  })

  loaders.push({
    locale: language,
    key: 'user',
    loader: async () => (await import(`./langs/${language}/userPage.json`)).default
  })

  loaders.push({
    locale: language,
    key: 'errors',
    loader: async () => (await import(`./langs/${language}/errors.json`)).default
  })

  loaders.push({
    locale: language,
    key: 'forms',
    loader: async () => (await import(`./langs/${language}/forms.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'contact',
    loader: async () => (await import(`./langs/${language}/contact.json`)).default
  })

  loaders.push({
    locale: language,
    key: 'services',
    loader: async () => (await import(`./langs/${language}/services.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'info',
    loader: async () => (await import(`./langs/${language}/info.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'serviceTypeText',
    loader: async () => (await import(`./langs/${language}/servicetypetext.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'cookies',
    loader: async () => (await import(`./langs/${language}/cookies.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'landing',
    loader: async () => (await import(`./langs/${language}/landing.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'lostAndFounds',
    loader: async () => (await import(`./langs/${language}/lostandfounds.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'islands_landing',
    loader: async () => (await import(`./langs/${language}/islands-landing.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'projects',
    loader: async () => (await import(`./langs/${language}/projects.json`)).default
  })
  loaders.push({
    locale: language,
    key: 'servicesSection',
    loader: async () => (await import(`./langs/${language}/servicesSection.json`)).default
  })

  // Example route config:
  //   - For the routes if you leave empty it is loaded in all routes.
  //   - For array routes you can use regex for search routes. It is loaded in all the routes that are define in the array.
  //
  // loaders.push({
  //   locale: language,
  //   key: 'example',
  //   routes: ['/example', /example\/?.*/, '/other-example-route'],
  //   loader: async () => (await import(`./langs/${language}/example.json`)).default
  // })

  return acc.concat(loaders)
}, [])

const config = {
  translations: {
    en: { lang: LANGS },
    es: { lang: LANGS },
    de: { lang: LANGS },
    it: { lang: LANGS },
    fr: { lang: LANGS }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loaders: loaders as any
}

export const defaultLocale = 'es'

function setLangCookie(lang: string, days: number) {
  if (typeof window !== 'undefined') {
    let expires = ''
    if (days) {
      const date = new Date()
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
      expires = '; expires=' + date.toUTCString()
    }

    document.cookie = 'lang=' + lang + expires + '; path=/'
  }
}

export function handleChangeLocale({ currentTarget }: { currentTarget: HTMLSelectElement }) {
  const { value } = currentTarget
  document.cookie = `lang=${value};`
}

export function changeLocale(lang: LangKeys) {
  if (typeof window !== 'undefined') {
    setLangCookie(lang, 90)
    locale.set(lang)
  }
}

export function isUrlLocalized(url: URL | string) {
  let pathname
  if (typeof url === 'string') {
    if (!url.startsWith('/')) {
      pathname = new URL(url).pathname
    } else {
      pathname = url
    }
  } else {
    pathname = url.pathname
  }

  return locales.get().some((lang) => pathname.startsWith(`/${lang}/`) || pathname === `/${lang}`)
}

export function localizeUrl(url: URL | string, lang: string): URL | string {
  let rUrl: URL

  if (typeof url === 'string') {
    rUrl = url.startsWith('/') ? new URL('https://mocking.com' + url) : new URL(url)
  } else {
    rUrl = new URL(url)
  }

  const [, maybeLang, ...rest] = rUrl.pathname.split('/')

  let newPathname = ''
  if (!maybeLang) {
    newPathname = `/${lang}`
  } else if (locales.get().some((l) => l === maybeLang)) {
    newPathname = `/${[lang, ...rest].join('/')}`
  } else {
    newPathname = `/${[lang, maybeLang, ...rest].join('/')}`
  }

  rUrl.pathname = newPathname

  if (typeof url === 'string') {
    return url.startsWith('/') ? rUrl.pathname : rUrl.toString()
  }

  return rUrl
}

export const { t, loading, locales, locale, translations, loadTranslations, addTranslations, setLocale, setRoute } = new i18n<
  Parser.Params<Payload>
>(config)
